import type { DeviceController } from "./devices/device-interface"
import { HandyDevice } from "./devices/handy-device"
import { ButtplugDevice } from "./devices/buttplug-device"
import { IntifaceDevice } from "./devices/intiface-device"
import type { DeviceType } from "./device-store"

class UnifiedDeviceController {
  private adapters: Map<string, DeviceController> = new Map()
  private currentAdapter: DeviceController | null = null

  // Get or create adapter for device type
  private getAdapter(deviceType: DeviceType): DeviceController {
    if (this.adapters.has(deviceType)) {
      return this.adapters.get(deviceType)!
    }

    let adapter: DeviceController

    switch (deviceType) {
      case "handy":
        adapter = new HandyDevice()
        break
      case "buttplug":
        adapter = new ButtplugDevice()
        break
      case "lovense":
      case "kiiroo":
      case "osr2":
      case "sr6":
      case "other":
        // These all use Intiface adapter
        adapter = new IntifaceDevice()
        break
      default:
        throw new Error(`Unsupported device type: ${deviceType}`)
    }

    this.adapters.set(deviceType, adapter)
    return adapter
  }

  // Set the active device. apiVersion is Handy-specific (stable v2 vs beta
  // v3) — duck-typed the same way manualStart/setSlideRange are elsewhere in
  // this class, since it's not part of the shared DeviceController interface.
  setActiveDevice(deviceType: DeviceType, connectionKey?: string, apiVersion?: "v2" | "v3") {
    this.currentAdapter = this.getAdapter(deviceType)
    if (connectionKey) {
      this.currentAdapter.setConnectionKey(connectionKey)
    }
    if (apiVersion && "setApiVersion" in this.currentAdapter) {
      ;(this.currentAdapter as any).setApiVersion(apiVersion)
    }
  }

  // Get current adapter or throw
  private requireAdapter(): DeviceController {
    if (!this.currentAdapter) {
      throw new Error("No active device set")
    }
    return this.currentAdapter
  }

  // Delegate all methods to current adapter
  // Note: duration is used by Intiface/Buttplug adapters. Handy computes its own duration internally.
  async setPosition(position: number, isRealTime?: boolean | number, _legacyIsRealTime?: boolean): Promise<boolean> {
    // Handle legacy 3-arg calls: setPosition(pos, duration, isRealTime)
    // In that case isRealTime is a number (duration) and _legacyIsRealTime is the actual bool
    const realTimeFlag: boolean = typeof isRealTime === "boolean"
      ? isRealTime
      : typeof _legacyIsRealTime === "boolean"
        ? _legacyIsRealTime
        : false
    const duration: number | undefined = typeof isRealTime === "number" ? isRealTime : undefined
    return this.requireAdapter().setPosition(position, duration, realTimeFlag)
  }

  async setVibration(intensity: number): Promise<boolean> {
    return this.requireAdapter().setVibration(intensity)
  }

  async setRotation(speed: number): Promise<boolean> {
    return this.requireAdapter().setRotation(speed)
  }

  async setOscillation(speed: number): Promise<boolean> {
    return this.requireAdapter().setOscillation(speed)
  }

  async setConstrict(level: number): Promise<boolean> {
    return this.requireAdapter().setConstrict(level)
  }

  async setSurge(position: number, duration?: number): Promise<boolean> {
    return this.requireAdapter().setSurge(position, duration)
  }

  async setSway(position: number, duration?: number): Promise<boolean> {
    return this.requireAdapter().setSway(position, duration)
  }

  async setRoll(position: number, duration?: number): Promise<boolean> {
    return this.requireAdapter().setRoll(position, duration)
  }

  async setPitch(position: number, duration?: number): Promise<boolean> {
    return this.requireAdapter().setPitch(position, duration)
  }

  async stop(): Promise<boolean> {
    return this.requireAdapter().stop()
  }

  setConnectionKey(key: string) {
    this.requireAdapter().setConnectionKey(key)
  }

  getConnectionKey(): string | null {
    return this.requireAdapter().getConnectionKey()
  }

  async checkConnection(): Promise<boolean> {
    return this.requireAdapter().checkConnection()
  }

  async getInfo(): Promise<any> {
    return this.requireAdapter().getInfo()
  }

  async getStatus(): Promise<any> {
    return this.requireAdapter().getStatus()
  }

  async testMovement(): Promise<boolean> {
    return this.requireAdapter().testMovement()
  }

  async autoCalibrate(pingCount?: number): Promise<{ latencyMs: number; serverTime: number }> {
    const adapter = this.requireAdapter()
    if (!adapter.autoCalibrate) {
      throw new Error("Device does not support calibration")
    }
    return adapter.autoCalibrate(pingCount)
  }

  startConnectionMonitor(onStatusChange: (connected: boolean) => void, intervalMs?: number) {
    this.requireAdapter().startConnectionMonitor(onStatusChange, intervalMs)
  }

  stopConnectionMonitor() {
    if (this.currentAdapter) {
      this.currentAdapter.stopConnectionMonitor()
    }
  }

  getIsConnected(): boolean {
    return this.requireAdapter().getIsConnected()
  }

  disconnect() {
    this.requireAdapter().disconnect()
  }

  async connect(): Promise<boolean> {
    const adapter = this.requireAdapter()
    if ("connect" in adapter) {
      return (adapter as any).connect()
    }
    return true
  }

  // Takes the device type explicitly rather than reading this.currentAdapter --
  // currentAdapter is a single shared slot, so if any other device's connect
  // attempt or background connection-monitor interleaves with this one (both
  // call setActiveDevice, which reassigns currentAdapter), reading the shared
  // slot right after a connect() call can silently return the WRONG adapter's
  // error, or null. Reading the per-type adapter directly from the cache
  // (populated by getAdapter) is race-proof.
  getLastConnectionError(deviceType?: DeviceType): string | null {
    const adapter = deviceType ? this.adapters.get(deviceType) : this.currentAdapter
    if (adapter?.getLastConnectionError) return adapter.getLastConnectionError()
    return null
  }

  // ── Manual / freeplay control ──────────────────────────────────────────────

  async manualStart(speed = 50): Promise<boolean> {
    const a = this.getCurrentAdapter()
    if (!a) return false
    if ("manualStart" in a) return (a as any).manualStart(speed)
    // Fallback: vibrators / other devices use vibration intensity
    return a.setVibration(speed / 100)
  }

  async manualStop(): Promise<boolean> {
    const a = this.getCurrentAdapter()
    if (!a) return false
    if ("manualStop" in a) return (a as any).manualStop()
    return a.stop()
  }

  async manualSetSpeed(speed: number): Promise<boolean> {
    const a = this.getCurrentAdapter()
    if (!a) return false
    if ("manualSetSpeed" in a) return (a as any).manualSetSpeed(speed)
    return a.setVibration(speed / 100)
  }

  async setStrokeRange(min: number, max: number): Promise<boolean> {
    const a = this.getCurrentAdapter()
    if (!a) return false
    if ("setSlideRange" in a) return (a as any).setSlideRange(min, max)
    return false
  }

  /** What the currently active device can actually do (linear/vibrate/rotate),
   * so the manual-control UI can show the right sliders instead of guessing
   * from device type. Only ButtplugDevice implements this today (it's the
   * only adapter connecting to hardware whose capabilities aren't known
   * ahead of time) -- everything else returns null and the UI falls back to
   * its existing per-type behavior. */
  getCapabilities(): { linear: boolean; vibrate: boolean; rotate: boolean } | null {
    const a = this.getCurrentAdapter()
    if (!a) return null
    if ("getCapabilities" in a) return (a as any).getCapabilities()
    return null
  }

  getManualActive(): boolean {
    const a = this.getCurrentAdapter()
    if (!a) return false
    if ("getIsHAMPActive" in a) return (a as any).getIsHAMPActive()
    return false
  }

  // ─────────────────────────────────────────────────────────────────────────────

  setDeviceSettings(settings: { minPosition?: number; maxPosition?: number }) {
    const adapter = this.getCurrentAdapter()
    if (adapter && "setDeviceSettings" in adapter) {
      ;(adapter as any).setDeviceSettings(settings)
    }
  }

  // Get the underlying adapter (for advanced use cases)
  getCurrentAdapter(): DeviceController | null {
    return this.currentAdapter
  }
}

export const deviceController = new UnifiedDeviceController()
